import { error, warning, neutral, success } from '_lex/_foundations/phxColors';
import { MetricStatus, TimeRangeFilter } from '../_types';

// Status thresholds for metric cards
export const STATUS_THRESHOLDS = {
  GOOD: 90,
  CONCERNING: 50,
} as const;

export const getMetricStatus = (percent: number): MetricStatus => {
  if (percent >= STATUS_THRESHOLDS.GOOD) return 'GOOD';
  if (percent >= STATUS_THRESHOLDS.CONCERNING) return 'CONCERNING';
  return 'CRITICAL';
};

// Status colors mapping
export const STATUS_COLORS = {
  GOOD: neutral[300],
  CONCERNING: warning[300],
  CRITICAL: error[800],
} as const;

// Chart colors for different data series
export const CHART_COLORS = {
  // Policy updates
  pendingUpdates: error[500],
  processed: success[600],
  
  // Acknowledgments/DTBs completed
  onTime: success[600],
  overdueByLessThanWeek: error[500],
  overdueByWeekOrMore: error[800],
  
  // DTB completion
  completedOnTime: success[600],
  completedLate: error[500],
  
  // Lexipol updates
  accepted: '#B6E0FF', // primary[200]
  modified: '#76C8FF', // primary[300]
  rejected: '#004B87', // primary[800]
  lexipolPending: '#CDD5DF', // neutral[300]
  
  // Assignments
  completedLine: success[600],
  overdueLine: error[700],
  completedArea: success[200],
  overdueArea: error[200],
} as const;

// Time range filter options with labels
export const TIME_RANGE_OPTIONS = [
  { value: TimeRangeFilter.PREVIOUS_MONTH, label: 'Previous Month' },
  { value: TimeRangeFilter.PAST_3_MONTHS, label: 'Past 3 months' },
  { value: TimeRangeFilter.YEAR_TO_DATE, label: 'Year-to-date' },
  { value: TimeRangeFilter.PAST_12_MONTHS, label: 'Past 12 months' },
  { value: TimeRangeFilter.LAST_CALENDAR_YEAR, label: 'Last Calendar Year' },
  { value: TimeRangeFilter.MONTH_TO_DATE, label: 'Month-to-date' },
  { value: TimeRangeFilter.PAST_30_DAYS, label: 'Past 30 days' },
  { value: TimeRangeFilter.PAST_2_YEARS, label: 'Past 2 years' },
  { value: TimeRangeFilter.PAST_3_YEARS, label: 'Past 3 years' },
] as const;

// Helper to get date range from filter
export const getDateRangeFromFilter = (filter: TimeRangeFilter): { startDate: string; endDate: string } => {
  const today = new Date();
  const endDate = new Date(today);
  let startDate = new Date(today);

  switch (filter) {
    case TimeRangeFilter.PREVIOUS_MONTH:
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate.setDate(0); // Last day of previous month
      break;
    case TimeRangeFilter.PAST_30_DAYS:
      startDate.setDate(today.getDate() - 30);
      break;
    case TimeRangeFilter.PAST_3_MONTHS:
      startDate.setMonth(today.getMonth() - 3);
      break;
    case TimeRangeFilter.YEAR_TO_DATE:
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
    case TimeRangeFilter.PAST_12_MONTHS:
      // Start from 12 months ago (first day of that month)
      startDate = new Date(today.getFullYear(), today.getMonth() - 12, 1);
      // End at last day of previous month (excluding current month)
      endDate.setMonth(today.getMonth(), 0);
      break;
    case TimeRangeFilter.LAST_CALENDAR_YEAR:
      startDate = new Date(today.getFullYear() - 1, 0, 1);
      endDate.setFullYear(today.getFullYear() - 1, 11, 31);
      break;
    case TimeRangeFilter.MONTH_TO_DATE:
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case TimeRangeFilter.PAST_2_YEARS:
      startDate.setFullYear(today.getFullYear() - 2);
      break;
    case TimeRangeFilter.PAST_3_YEARS:
      startDate.setFullYear(today.getFullYear() - 3);
      break;
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

// Mock manual types
export const MOCK_MANUALS = [
  { id: 'all', name: 'All' },
  { id: 'policy', name: 'Policy Manual' },
  { id: 'fire', name: 'Fire Manual' },
  { id: 'ems', name: 'EMS Manual' },
] as const;

